import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Tag, Checkbox, DatePicker, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, DollarOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_LOCATIONS, GET_ALL_ORGANIZATIONS, GET_ALL_PRICING_TEMPLATES, GET_LOCATION_PRICING_ASSIGNMENTS } from '../graphql/queries';
import {
  CREATE_PARKING,
  UPDATE_PARKING,
  DELETE_PARKING,
  CREATE_SECTOR,
  UPDATE_SECTOR,
  DELETE_SECTOR,
  CREATE_SLOT,
  UPDATE_SLOT,
  DELETE_SLOT,
  CREATE_ORGANIZATION,
  SEND_DEVICE_COMMAND,
  CREATE_PRICING_ASSIGNMENT,
  UPDATE_PRICING_ASSIGNMENT,
  DELETE_PRICING_ASSIGNMENT,
} from '../graphql/mutations';
import dayjs from 'dayjs';

const { Option } = Select;

type LocationType = 'PARKING' | 'SECTOR' | 'SLOT';

export const Parkings: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isOrgModalVisible, setIsOrgModalVisible] = useState(false);
  const [isCommandModalVisible, setIsCommandModalVisible] = useState(false);
  const [isPricingModalVisible, setIsPricingModalVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [selectedParkingId, setSelectedParkingId] = useState<string | undefined>(undefined);
  const [selectedLocationForPricing, setSelectedLocationForPricing] = useState<any>(null);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [modalType, setModalType] = useState<LocationType>('PARKING');
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [form] = Form.useForm();
  const [orgForm] = Form.useForm();
  const [pricingForm] = Form.useForm();

  const { loading, data, refetch } = useQuery(GET_ALL_LOCATIONS);
  const { data: orgsData, refetch: refetchOrgs } = useQuery(GET_ALL_ORGANIZATIONS);
  const { data: templatesData } = useQuery(GET_ALL_PRICING_TEMPLATES);

  // Fetch pricing assignments for selected location
  const { data: assignmentsData, refetch: refetchAssignments } = useQuery(GET_LOCATION_PRICING_ASSIGNMENTS, {
    variables: { locationId: selectedLocationForPricing?.id },
    skip: !selectedLocationForPricing?.id,
  });

  const [createParking] = useMutation(CREATE_PARKING);
  const [updateParking] = useMutation(UPDATE_PARKING);
  const [deleteParking] = useMutation(DELETE_PARKING);
  const [createSector] = useMutation(CREATE_SECTOR);
  const [updateSector] = useMutation(UPDATE_SECTOR);
  const [deleteSector] = useMutation(DELETE_SECTOR);
  const [createSlot] = useMutation(CREATE_SLOT);
  const [updateSlot] = useMutation(UPDATE_SLOT);
  const [deleteSlot] = useMutation(DELETE_SLOT);
  const [createOrganization] = useMutation(CREATE_ORGANIZATION);
  const [sendDeviceCommand] = useMutation(SEND_DEVICE_COMMAND);
  const [createPricingAssignment] = useMutation(CREATE_PRICING_ASSIGNMENT);
  const [updatePricingAssignment] = useMutation(UPDATE_PRICING_ASSIGNMENT);
  const [deletePricingAssignment] = useMutation(DELETE_PRICING_ASSIGNMENT);

  // Auto-calculate slot count when viewing sector
  useEffect(() => {
    if (modalType === 'SECTOR' && editingLocation) {
      const slots = data?.getAllLocations?.filter(
        (l: any) => l.type === 'SLOT' && l.parent?.id === editingLocation.id
      ) || [];
      form.setFieldsValue({ totalSlots: slots.length });
    }
  }, [editingLocation, modalType, data, form]);

  const handleAdd = (type: LocationType, parentId?: string) => {
    setEditingLocation(null);
    setModalType(type);
    form.resetFields();
    if (parentId) {
      form.setFieldsValue({ parentId });
    }
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingLocation(record);
    setModalType(record.type);

    // Convert coordinates object to string format for form
    let coordinatesString = null;
    if (record.coordinates && record.coordinates.latitude && record.coordinates.longitude) {
      coordinatesString = `${record.coordinates.latitude}, ${record.coordinates.longitude}`;
    }

    // Calculate slot count for sectors
    if (record.type === 'SECTOR') {
      const slots = data?.getAllLocations?.filter(
        (l: any) => l.type === 'SLOT' && l.parent?.id === record.id
      ) || [];
      form.setFieldsValue({
        ...record,
        organizationId: record.organization?.id,
        parentId: record.parent?.id,
        totalSlots: slots.length,
        autodiscovered: record.autodiscovered || false,
        coordinates: coordinatesString,
        enabled: record.enabled !== false, // Default to true if undefined
      });
    } else if (record.type === 'SLOT') {
      // For slots, find the parent parking to set selectedParkingId
      const parentSector = data?.getAllLocations?.find((l: any) => l.id === record.parent?.id);
      const parkingId = parentSector?.parent?.id;
      setSelectedParkingId(parkingId);

      form.setFieldsValue({
        ...record,
        organizationId: record.organization?.id,
        parentId: record.parent?.id,
        selectedParkingId: parkingId,
        autodiscovered: record.autodiscovered || false,
        coordinates: coordinatesString,
        enabled: record.enabled !== false, // Default to true if undefined
      });
    } else {
      // For parkings
      form.setFieldsValue({
        ...record,
        organizationId: record.organization?.id,
        parentId: record.parent?.id,
        autodiscovered: record.autodiscovered || false,
        coordinates: coordinatesString,
        enabled: record.enabled !== false, // Default to true if undefined
      });
    }
    setIsModalVisible(true);
  };

  const handleDelete = async (record: any) => {
    try {
      const mutationMap = {
        PARKING: deleteParking,
        SECTOR: deleteSector,
        SLOT: deleteSlot,
      };
      await mutationMap[record.type as LocationType]({ variables: { id: record.id } });
      message.success(`${record.type} deleted successfully`);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      const values = await orgForm.validateFields();
      const result = await createOrganization({
        variables: {
          input: {
            name: values.name,
            email: values.email || `${values.name.toLowerCase().replace(/\s/g, '')}@example.com`,
            revenueShareRate: values.revenueShareRate || 0.15,
            enabled: true,
          },
        },
      });

      message.success('Organization created successfully');
      await refetchOrgs();
      setIsOrgModalVisible(false);
      orgForm.resetFields();

      if (result.data?.createOrganization) {
        form.setFieldsValue({ organizationId: result.data.createOrganization.id });
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  // Handle unassigning a device (move slot back to AUTO-DISCOVERY sector)
  const handleUnassignDevice = async () => {
    if (!editingLocation || editingLocation.type !== 'SLOT') return;

    // Find the AUTO-DISCOVERY-DEVICES sector
    const autoDiscoverySector = data?.getAllLocations?.find(
      (l: any) => l.type === 'SECTOR' && l.code === 'AUTO-DISCOVERY-DEVICES'
    );

    if (!autoDiscoverySector) {
      message.error('Auto-discovery sector not found. Cannot unassign device.');
      return;
    }

    try {
      // Update slot to move it to auto-discovery sector
      // Keep deviceId and serialNumber as they are hardcoded in the device
      await updateSlot({
        variables: {
          id: editingLocation.id,
          input: {
            parentId: autoDiscoverySector.id,
            name: `Device ${editingLocation.deviceId}`, // Reset name to device ID
            code: `DEV-${editingLocation.deviceId}`, // Reset code
            enabled: false, // Disable until reassigned
            metadata: { autoDiscovered: true },
          },
        },
      });

      message.success('Device unassigned and moved to auto-discovery');
      setIsModalVisible(false);
      form.resetFields();
      setEditingLocation(null);
      refetch();
    } catch (error: any) {
      message.error(error.message || 'Failed to unassign device');
    }
  };

  const handleSendCommand = async (command: string) => {
    if (!selectedSlot || !selectedSlot.deviceId) {
      message.error('No device ID available for this slot');
      return;
    }

    try {
      const result = await sendDeviceCommand({
        variables: {
          deviceId: selectedSlot.deviceId,
          command,
        },
      });

      if (result.data?.sendDeviceCommand?.success) {
        message.success(result.data.sendDeviceCommand.message);
      } else {
        message.error(result.data?.sendDeviceCommand?.message || 'Command failed');
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleShowCommands = (slot: any) => {
    setSelectedSlot(slot);
    setIsCommandModalVisible(true);
  };

  // Pricing assignment handlers
  const handleShowPricing = (location: any) => {
    setSelectedLocationForPricing(location);
    setEditingAssignment(null);
    pricingForm.resetFields();
    setIsPricingModalVisible(true);
  };

  const handleAddAssignment = () => {
    setEditingAssignment(null);
    pricingForm.resetFields();
    pricingForm.setFieldsValue({
      validFrom: dayjs(),
      enabled: true,
      priority: 0,
    });
  };

  const handleEditAssignment = (assignment: any) => {
    setEditingAssignment(assignment);
    pricingForm.setFieldsValue({
      pricingTemplateId: assignment.pricingTemplate?.id,
      validFrom: dayjs(assignment.validFrom),
      validUntil: assignment.validUntil ? dayjs(assignment.validUntil) : null,
      enabled: assignment.enabled,
      priority: assignment.priority,
    });
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    try {
      await deletePricingAssignment({ variables: { id: assignmentId } });
      message.success('Pricing assignment deleted');
      refetchAssignments();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleSavePricingAssignment = async () => {
    try {
      const values = await pricingForm.validateFields();

      const input = {
        pricingTemplateId: values.pricingTemplateId,
        validFrom: values.validFrom.toISOString(),
        validUntil: values.validUntil ? values.validUntil.toISOString() : null,
        enabled: values.enabled !== false,
        priority: values.priority || 0,
      };

      if (editingAssignment) {
        await updatePricingAssignment({
          variables: { id: editingAssignment.id, input },
        });
        message.success('Pricing assignment updated');
      } else {
        await createPricingAssignment({
          variables: {
            input: {
              ...input,
              locationId: selectedLocationForPricing.id,
            },
          },
        });
        message.success('Pricing assignment created');
      }

      setEditingAssignment(null);
      pricingForm.resetFields();
      refetchAssignments();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Clean and validate polygon format BEFORE creating sector
      if (modalType === 'SECTOR' && values.polygon) {
        // Remove extra whitespace and newlines
        values.polygon = values.polygon.replace(/\s+/g, ' ').trim();

        if (!values.polygon.toUpperCase().startsWith('POLYGON')) {
          message.error('Invalid polygon format. Must start with POLYGON(...)');
          return; // STOP HERE - Don't create the sector
        }
      }

      // Parse coordinates for parking and slots
      if (modalType === 'PARKING') {
        if (values.coordinates) {
          const [lat, lng] = values.coordinates.split(',').map((s: string) => parseFloat(s.trim()));
          if (isNaN(lat) || isNaN(lng)) {
            message.error('Invalid coordinates format');
            return;
          }
          values.latitude = lat;
          values.longitude = lng;
        }
        // Always delete coordinates field (it's not in the schema)
        delete values.coordinates;
      }

      if (modalType === 'SLOT') {
        if (values.coordinates) {
          const [lat, lng] = values.coordinates.split(',').map((s: string) => parseFloat(s.trim()));
          if (isNaN(lat) || isNaN(lng)) {
            message.error('Invalid coordinates format');
            return;
          }
          values.latitude = lat;
          values.longitude = lng;
        }
        // Always delete coordinates field (it's not in the schema)
        delete values.coordinates;
      }

      // For parkings, handle special fields
      if (modalType === 'PARKING') {
        // When updating a parking, remove organizationId (not in UpdateParkingInput)
        if (editingLocation) {
          delete values.organizationId;
        }
      }

      // For sectors, handle special fields
      if (modalType === 'SECTOR') {
        // totalSlots is auto-calculated
        delete values.totalSlots;

        // When updating a sector, remove parentId and organizationId (not in UpdateSectorInput)
        if (editingLocation) {
          delete values.parentId;
          delete values.organizationId;
        }
      }

      // For slots, handle special fields
      if (modalType === 'SLOT') {
        // Remove selectedDevice (UI only)
        delete values.selectedDevice;

        // Remove selectedParkingId (UI only, used for filtering sectors)
        delete values.selectedParkingId;

        // Remove description (not in CreateSlotInput)
        delete values.description;

        // Move autodiscovered flag to metadata
        if (values.autodiscovered !== undefined) {
          values.metadata = {
            ...(values.metadata || {}),
            autoDiscovered: values.autodiscovered,
          };
          delete values.autodiscovered;
        }
      }

      const mutationMap = {
        PARKING: { create: createParking, update: updateParking },
        SECTOR: { create: createSector, update: updateSector },
        SLOT: { create: createSlot, update: updateSlot },
      };

      if (editingLocation) {
        await mutationMap[modalType].update({
          variables: { id: editingLocation.id, input: values },
        });
        message.success(`${modalType} updated successfully`);
      } else {
        await mutationMap[modalType].create({
          variables: { input: values },
        });
        message.success(`${modalType} created successfully`);
      }

      setIsModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  // Build hierarchical data structure for table (sorted alphabetically by name)
  const buildHierarchy = (locations: any[]) => {
    const parkings = locations?.filter((l) => l.type === 'PARKING') || [];

    // Sort parkings alphabetically by name
    const sortedParkings = [...parkings].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );

    return sortedParkings.map((parking) => {
      const sectors =
        locations?.filter((l) => l.type === 'SECTOR' && l.parent?.id === parking.id) || [];

      // Sort sectors alphabetically by name
      const sortedSectors = [...sectors].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );

      return {
        ...parking,
        children: sortedSectors.map((sector) => {
          const slots =
            locations?.filter((l) => l.type === 'SLOT' && l.parent?.id === sector.id) || [];

          // Sort slots alphabetically by name
          const sortedSlots = [...slots].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          );

          return {
            ...sector,
            slotCount: sortedSlots.length, // Auto-calculated slot count
            children: sortedSlots,
          };
        }),
      };
    });
  };

  // Helper to find active assignment for a location
  const findActiveAssignment = (location: any) => {
    const assignments = location?.pricingAssignments || [];
    const now = new Date();

    return assignments.find((a: any) => {
      if (!a.enabled) return false;
      const validFrom = new Date(a.validFrom);
      const validUntil = a.validUntil ? new Date(a.validUntil) : null;
      return validFrom <= now && (!validUntil || validUntil >= now);
    });
  };

  // Get effective pricing (direct or inherited) for a location
  const getEffectivePricing = (location: any, allLocations: any[]) => {
    // Check direct assignment first
    const directAssignment = findActiveAssignment(location);
    if (directAssignment) {
      return {
        assignment: directAssignment,
        source: 'direct',
        sourceName: null,
      };
    }

    // Check parent (for slots → sector, for sectors → parking)
    if (location.parent?.id) {
      const parent = allLocations?.find((l: any) => l.id === location.parent.id);
      if (parent) {
        const parentAssignment = findActiveAssignment(parent);
        if (parentAssignment) {
          return {
            assignment: parentAssignment,
            source: 'inherited',
            sourceName: parent.name,
            sourceType: parent.type,
          };
        }

        // Check grandparent (for slots → parking)
        if (parent.parent?.id) {
          const grandparent = allLocations?.find((l: any) => l.id === parent.parent.id);
          if (grandparent) {
            const grandparentAssignment = findActiveAssignment(grandparent);
            if (grandparentAssignment) {
              return {
                assignment: grandparentAssignment,
                source: 'inherited',
                sourceName: grandparent.name,
                sourceType: grandparent.type,
              };
            }
          }
        }
      }
    }

    return null;
  };

  // Get active pricing display for a location
  const getActivePricingDisplay = (location: any) => {
    const allLocations = data?.getAllLocations || [];
    const effective = getEffectivePricing(location, allLocations);
    const directAssignments = location.pricingAssignments || [];

    if (effective) {
      const template = effective.assignment.pricingTemplate;
      const templateName = template?.name || 'Unknown';

      if (effective.source === 'direct') {
        return (
          <Tag color="green" style={{ cursor: 'pointer' }}>
            {templateName}
          </Tag>
        );
      } else {
        // Inherited
        return (
          <Tag color="blue" style={{ cursor: 'pointer' }} title={`From ${effective.sourceType}: ${effective.sourceName}`}>
            {templateName} <span style={{ fontSize: '10px', opacity: 0.8 }}>({effective.sourceType?.charAt(0)})</span>
          </Tag>
        );
      }
    }

    // Check if any direct assignments exist (future or past but not currently active)
    if (directAssignments.length > 0) {
      return (
        <Tag color="orange" style={{ cursor: 'pointer' }}>
          {directAssignments.length} assignment(s)
        </Tag>
      );
    }

    return (
      <Tag color="default" style={{ cursor: 'pointer' }}>
        Default
      </Tag>
    );
  };

  const hierarchicalData = buildHierarchy(data?.getAllLocations || []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const colorMap = { PARKING: 'blue', SECTOR: 'green', SLOT: 'orange' };
        return <Tag color={colorMap[type as keyof typeof colorMap]}>{type}</Tag>;
      },
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_: any, record: any) => record.organization?.name || '-',
    },
    {
      title: 'Pricing',
      key: 'pricing',
      render: (_: any, record: any) => (
        <span onClick={() => handleShowPricing(record)}>
          {getActivePricingDisplay(record)}
        </span>
      ),
    },
    {
      title: 'Slots',
      key: 'slotCount',
      render: (_: any, record: any) => (record.type === 'SECTOR' ? record.slotCount || 0 : '-'),
    },
    {
      title: 'Device ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
      render: (deviceId: number) => deviceId || '-',
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Active' : 'Disabled'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          {record.type === 'PARKING' && (
            <Button onClick={() => handleAdd('SECTOR', record.id)} size="small">
              Add Sector
            </Button>
          )}
          {record.type === 'SECTOR' && (
            <Button onClick={() => handleAdd('SLOT', record.id)} size="small">
              Add Slot
            </Button>
          )}
          {record.type === 'SLOT' && record.deviceId && (
            <Button
              icon={<ThunderboltOutlined />}
              onClick={() => handleShowCommands(record)}
              size="small"
              type="dashed"
            >
              Commands
            </Button>
          )}
          <Button
            icon={<DollarOutlined />}
            onClick={() => handleShowPricing(record)}
            size="small"
            type="dashed"
          />
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Are you sure to delete this location?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Parkings</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd('PARKING')}>
          Add Parking
        </Button>
      </div>

      {/* Pricing Legend */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 16, alignItems: 'center', fontSize: '12px', color: '#666' }}>
        <span><strong>Pricing:</strong></span>
        <span><Tag color="green" style={{ marginRight: 4 }}>Name</Tag> Direct assignment</span>
        <span><Tag color="blue" style={{ marginRight: 4 }}>Name (P)</Tag> Inherited from Parking</span>
        <span><Tag color="blue" style={{ marginRight: 4 }}>Name (S)</Tag> Inherited from Sector</span>
        <span><Tag color="orange" style={{ marginRight: 4 }}>N</Tag> Has future/past assignments</span>
        <span><Tag color="default" style={{ marginRight: 4 }}>Default</Tag> Uses $5/hr default</span>
      </div>

      <Table
        loading={loading}
        dataSource={hierarchicalData}
        columns={columns}
        rowKey="id"
        expandable={{ defaultExpandAllRows: false }}
      />

      <Modal
        title={
          editingLocation ? `Edit ${modalType}` : `Add ${modalType}`
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          {/* Common fields */}
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Code" name="code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>

          {/* Enabled checkbox - only show when editing */}
          {editingLocation && (
            <Form.Item name="enabled" valuePropName="checked">
              <Checkbox>Enabled</Checkbox>
            </Form.Item>
          )}

          {/* Parking-specific fields */}
          {modalType === 'PARKING' && (
            <>
              <Form.Item
                label="Organization"
                name="organizationId"
                rules={[{ required: true }]}
              >
                <Select
                  placeholder="Select organization"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => setIsOrgModalVisible(true)}
                          style={{ width: '100%', textAlign: 'left' }}
                        >
                          Create New Organization
                        </Button>
                      </div>
                    </>
                  )}
                >
                  {orgsData?.getAllOrganizations.map((org: any) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Address"
                name="address"
                help="Physical address of the parking (e.g., 123 Main St, Miami, FL 33101)"
              >
                <Input placeholder="Enter parking address" />
              </Form.Item>

              <Form.Item
                label="Coordinates (lat, lng)"
                name="coordinates"
                rules={[{ required: true }]}
                help="Example: 25.963074, -80.126208"
              >
                <Input placeholder="25.963074, -80.126208" />
              </Form.Item>

              <Form.Item label="Total Slots" name="totalSlots">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          {/* Sector-specific fields */}
          {modalType === 'SECTOR' && (
            <>
              <Form.Item label="Parent Parking" name="parentId" rules={[{ required: true }]}>
                <Select>
                  {data?.getAllLocations
                    .filter((l: any) => l.type === 'PARKING')
                    .map((parking: any) => (
                      <Option key={parking.id} value={parking.id}>
                        {parking.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item label="Organization" name="organizationId" rules={[{ required: true }]}>
                <Select
                  placeholder="Select organization"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => setIsOrgModalVisible(true)}
                          style={{ width: '100%', textAlign: 'left' }}
                        >
                          Create New Organization
                        </Button>
                      </div>
                    </>
                  )}
                >
                  {orgsData?.getAllOrganizations.map((org: any) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Polygon (WKT format)"
                name="polygon"
                help="Format: POLYGON((lng lat, lng lat, ...)) - Use LONGITUDE first, then LATITUDE. First and last points must be identical to close the polygon."
                rules={[
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) return Promise.resolve();

                      // Remove extra whitespace and newlines
                      const cleanValue = value.replace(/\s+/g, ' ').trim();

                      // Check if it starts with POLYGON
                      if (!cleanValue.toUpperCase().startsWith('POLYGON')) {
                        return Promise.reject('Polygon must start with POLYGON((...))');
                      }

                      // Basic validation for format
                      if (!cleanValue.includes('((') || !cleanValue.includes('))')) {
                        return Promise.reject('Invalid polygon format. Use POLYGON((...))');
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="POLYGON((-80.123 25.939, -80.122 25.939, -80.122 25.940, -80.123 25.940, -80.123 25.939))"
                />
              </Form.Item>

              <Form.Item label="Total Slots (auto-calculated)" name="totalSlots">
                <InputNumber disabled min={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          {/* Slot-specific fields */}
          {modalType === 'SLOT' && (
            <>
              {editingLocation && (
                <Form.Item label="Parent Parking" name="selectedParkingId">
                  <Select
                    placeholder="Select parking to filter sectors"
                    onChange={(value) => {
                      setSelectedParkingId(value);
                      // Clear the sector selection when parking changes
                      form.setFieldsValue({ parentId: undefined });
                    }}
                    allowClear
                  >
                    {data?.getAllLocations
                      .filter((l: any) => l.type === 'PARKING')
                      .map((parking: any) => (
                        <Option key={parking.id} value={parking.id}>
                          {parking.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item label="Parent Sector" name="parentId" rules={[{ required: true }]}>
                <Select>
                  {data?.getAllLocations
                    .filter((l: any) =>
                      l.type === 'SECTOR' &&
                      (!selectedParkingId || l.parent?.id === selectedParkingId)
                    )
                    .map((sector: any) => (
                      <Option key={sector.id} value={sector.id}>
                        {sector.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item label="Organization" name="organizationId" rules={[{ required: true }]}>
                <Select
                  placeholder="Select organization"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => setIsOrgModalVisible(true)}
                          style={{ width: '100%', textAlign: 'left' }}
                        >
                          Create New Organization
                        </Button>
                      </div>
                    </>
                  )}
                >
                  {orgsData?.getAllOrganizations.map((org: any) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Available Device"
                name="selectedDevice"
                help="Select from autodiscovered devices or enter manually below"
              >
                <Select
                  placeholder="Select an autodiscovered device"
                  allowClear
                  onChange={(value) => {
                    if (value) {
                      const device = data?.getAllLocations?.find((l: any) => l.id === value);
                      if (device) {
                        // Auto-populate only device identifiers (not name/code - user will set those)
                        form.setFieldsValue({
                          serialNumber: device.serialNumber,
                          deviceId: device.deviceId,
                          autodiscovered: true,
                        });
                      }
                    } else {
                      // Clear fields when deselecting
                      form.setFieldsValue({
                        autodiscovered: false,
                      });
                    }
                  }}
                >
                  {(() => {
                    const allSlots = data?.getAllLocations?.filter((l: any) => l.type === 'SLOT') || [];

                    const autodiscoveredDevices = allSlots.filter((l: any) => {
                      // Parse metadata if it's a string
                      let metadata = l.metadata;
                      if (typeof metadata === 'string') {
                        try {
                          metadata = JSON.parse(metadata);
                        } catch (e) {
                          // ignore parse errors
                        }
                      }

                      return metadata?.autoDiscovered === true && l.parent?.code === 'AUTO-DISCOVERY-DEVICES';
                    });

                    return autodiscoveredDevices.map((device: any) => (
                      <Option key={device.id} value={device.id}>
                        Device {device.deviceId} - Serial: {device.serialNumber || 'N/A'}
                        {device.name && ` (${device.name})`}
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>

              <Form.Item
                label="Serial Number"
                name="serialNumber"
                rules={[{ required: true }]}
              >
                <Input placeholder="Or enter manually" />
              </Form.Item>

              <Form.Item label="Device ID" name="deviceId" rules={[{ required: true }]}>
                <InputNumber placeholder="Or enter manually" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Coordinates (lat, lng)"
                name="coordinates"
                help="Example: 25.963074, -80.126208"
              >
                <Input placeholder="25.963074, -80.126208" />
              </Form.Item>

              <Form.Item name="autodiscovered" valuePropName="checked">
                <Checkbox disabled>Autodiscovered (auto-set when selecting from list)</Checkbox>
              </Form.Item>

              {/* Unassign Device button - only when editing an existing slot */}
              {editingLocation && editingLocation.deviceId && (
                <Form.Item>
                  <Popconfirm
                    title="Unassign this device?"
                    description="The slot will be moved back to auto-discovery. Device ID and serial number will be preserved."
                    onConfirm={handleUnassignDevice}
                    okText="Yes, Unassign"
                    cancelText="Cancel"
                  >
                    <Button danger block>
                      Unassign Device (Move to Auto-Discovery)
                    </Button>
                  </Popconfirm>
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>

      {/* Create Organization Modal */}
      <Modal
        title="Create New Organization"
        open={isOrgModalVisible}
        onOk={handleCreateOrganization}
        onCancel={() => {
          setIsOrgModalVisible(false);
          orgForm.resetFields();
        }}
        width={400}
      >
        <Form form={orgForm} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input placeholder="Organization name" />
          </Form.Item>

          <Form.Item label="Email" name="email">
            <Input type="email" placeholder="contact@example.com" />
          </Form.Item>

          <Form.Item label="Revenue Share Rate" name="revenueShareRate">
            <InputNumber
              min={0}
              max={1}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="0.15"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Device Commands Modal */}
      <Modal
        title={`Barrier Commands - ${selectedSlot?.name || 'Slot'}`}
        open={isCommandModalVisible}
        onCancel={() => setIsCommandModalVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p>
            <strong>Device ID:</strong> {selectedSlot?.deviceId || 'N/A'}
          </p>
          <p>
            <strong>Serial Number:</strong> {selectedSlot?.serialNumber || 'N/A'}
          </p>
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', marginTop: '8px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => handleSendCommand('up')}
                block
                style={{ background: '#52c41a' }}
              >
                Raise Barrier (UP)
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => handleSendCommand('down')}
                block
                danger
              >
                Lower Barrier (DOWN)
              </Button>
              <Button
                type="default"
                onClick={() => handleSendCommand('status')}
                block
              >
                Get Status
              </Button>
              <Button
                type="default"
                onClick={() => handleSendCommand('reset')}
                block
              >
                Reset Device
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Pricing Assignment Modal */}
      <Modal
        title={`Pricing - ${selectedLocationForPricing?.name || 'Location'}`}
        open={isPricingModalVisible}
        onCancel={() => {
          setIsPricingModalVisible(false);
          setSelectedLocationForPricing(null);
          setEditingAssignment(null);
          pricingForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <div>
          <p style={{ color: '#666', marginBottom: 16 }}>
            <strong>Type:</strong> {selectedLocationForPricing?.type} |
            <strong> Inheritance:</strong> Slot inherits Sector inherits Parking
          </p>

          {/* Existing Assignments List */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>Assignments</strong>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddAssignment}>
                Add Assignment
              </Button>
            </div>

            {assignmentsData?.getLocationPricingAssignments?.length > 0 ? (
              <Table
                dataSource={assignmentsData.getLocationPricingAssignments}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: 'Template',
                    key: 'template',
                    render: (_: any, record: any) => record.pricingTemplate?.name || '-',
                  },
                  {
                    title: 'Valid From',
                    key: 'validFrom',
                    render: (_: any, record: any) => dayjs(record.validFrom).format('YYYY-MM-DD HH:mm'),
                  },
                  {
                    title: 'Valid Until',
                    key: 'validUntil',
                    render: (_: any, record: any) => record.validUntil ? dayjs(record.validUntil).format('YYYY-MM-DD HH:mm') : 'No end',
                  },
                  {
                    title: 'Priority',
                    dataIndex: 'priority',
                    key: 'priority',
                  },
                  {
                    title: 'Status',
                    key: 'enabled',
                    render: (_: any, record: any) => (
                      <Tag color={record.enabled ? 'green' : 'red'}>
                        {record.enabled ? 'Active' : 'Disabled'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_: any, record: any) => (
                      <Space size="small">
                        <Button size="small" onClick={() => handleEditAssignment(record)}>
                          Edit
                        </Button>
                        <Popconfirm
                          title="Delete this assignment?"
                          onConfirm={() => handleDeleteAssignment(record.id)}
                        >
                          <Button size="small" danger>Delete</Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            ) : (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No pricing assignments. This location will inherit pricing from its parent.
              </p>
            )}
          </div>

          {/* Add/Edit Assignment Form */}
          {(editingAssignment || pricingForm.getFieldValue('pricingTemplateId') !== undefined) && (
            <>
              <Divider>{editingAssignment ? 'Edit Assignment' : 'New Assignment'}</Divider>
              <Form form={pricingForm} layout="vertical">
                <Form.Item
                  label="Pricing Template"
                  name="pricingTemplateId"
                  rules={[{ required: true, message: 'Please select a template' }]}
                >
                  <Select placeholder="Select a pricing template">
                    {templatesData?.getAllPricingTemplates?.map((template: any) => (
                      <Option key={template.id} value={template.id}>
                        {template.name} ({template.pricingMode} - {template.currency} {template.baseRate}/hr)
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Space style={{ width: '100%' }} size="middle">
                  <Form.Item
                    label="Valid From"
                    name="validFrom"
                    rules={[{ required: true }]}
                    style={{ flex: 1 }}
                  >
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    label="Valid Until"
                    name="validUntil"
                    style={{ flex: 1 }}
                  >
                    <DatePicker showTime style={{ width: '100%' }} placeholder="No end date" />
                  </Form.Item>
                </Space>

                <Space style={{ width: '100%' }} size="middle">
                  <Form.Item
                    label="Priority"
                    name="priority"
                    tooltip="Higher priority wins if multiple assignments overlap"
                    style={{ flex: 1 }}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item name="enabled" valuePropName="checked" style={{ flex: 1, marginTop: 30 }}>
                    <Checkbox>Enabled</Checkbox>
                  </Form.Item>
                </Space>

                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={handleSavePricingAssignment}>
                      {editingAssignment ? 'Update' : 'Create'} Assignment
                    </Button>
                    <Button onClick={() => {
                      setEditingAssignment(null);
                      pricingForm.resetFields();
                    }}>
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
